/**
 * Security Management Component
 * ZO Bull Algo Trading Platform
 * 
 * Admin interface for managing security settings, blocked IPs, and monitoring.
 * Version: 6.2.0
 */
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './SecurityManagement.css';

interface SecurityStatus {
  status: string;
  timestamp: string;
  features: {
    rate_limiting: boolean;
    sql_injection_protection: boolean;
    xss_protection: boolean;
    csrf_protection: boolean;
    ddos_protection: boolean;
    security_headers: boolean;
    input_validation: boolean;
  };
  blocked_ips: string[];
  threat_detection: {
    active_monitoring: boolean;
    last_scan: string;
  };
}

interface BlockedIP {
  ip: string;
  reason?: string;
  blocked_at?: string;
}

interface ClientScore {
  ip_address: string;
  score: number;
  trust_level: string;
  recommendation: string;
}

interface SecurityHeader {
  name: string;
  description: string;
  recommended: boolean;
  current: string;
}

const SecurityManagement: React.FC = () => {
  const { t } = useTranslation();
  const [activeSection, setActiveSection] = useState('overview');
  const [securityStatus, setSecurityStatus] = useState<SecurityStatus | null>(null);
  const [blockedIPs, setBlockedIPs] = useState<BlockedIP[]>([]);
  const [clientScores, setClientScores] = useState<ClientScore[]>([]);
  const [headers, setHeaders] = useState<Record<string, SecurityHeader>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  
  // Form states
  const [blockIP, setBlockIP] = useState('');
  const [blockReason, setBlockReason] = useState('');
  const [blockDuration, setBlockDuration] = useState(300);
  const [checkIP, setCheckIP] = useState('');
  const [searchIP, setSearchIP] = useState('');

  useEffect(() => {
    fetchSecurityStatus();
    fetchBlockedIPs();
    fetchSecurityHeaders();
  }, []);

  const getAuthHeaders = () => ({
    'Authorization': `Bearer ${localStorage.getItem('admin_token')}`,
    'Content-Type': 'application/json'
  });

  const fetchSecurityStatus = async () => {
    try {
      const response = await fetch('/api/security/status', {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setSecurityStatus(data);
      } else if (response.status === 403) {
        setError('Admin access required');
      }
    } catch (err) {
      console.error('Failed to fetch security status:', err);
      setError('Failed to load security status');
    } finally {
      setLoading(false);
    }
  };

  const fetchBlockedIPs = async () => {
    try {
      const response = await fetch('/api/security/blocked-ips', {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setBlockedIPs((data.ips || []).map((ip: string) => ({ ip })));
      }
    } catch (err) {
      console.error('Failed to fetch blocked IPs:', err);
    }
  };

  const fetchClientScore = async (ip: string) => {
    try {
      const response = await fetch(`/api/security/client-score/${ip}`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setClientScores(prev => {
          const filtered = prev.filter(s => s.ip_address !== ip);
          return [...filtered, data];
        });
      }
    } catch (err) {
      console.error('Failed to fetch client score:', err);
    }
  };

  const fetchSecurityHeaders = async () => {
    try {
      const response = await fetch('/api/security/headers');
      if (response.ok) {
        const data = await response.json();
        setHeaders(data.security_headers || {});
      }
    } catch (err) {
      console.error('Failed to fetch security headers:', err);
    }
  };

  const handleBlockIP = async () => {
    if (!blockIP.trim()) return;
    
    setActionLoading('blocking');
    try {
      const response = await fetch('/api/security/block-ip', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          ip_address: blockIP.trim(),
          duration_seconds: blockDuration,
          reason: blockReason || 'Manual block by admin',
          permanent: blockDuration === -1
        })
      });
      
      if (response.ok) {
        setBlockedIPs(prev => [...prev, { ip: blockIP.trim(), reason: blockReason }]);
        setBlockIP('');
        setBlockReason('');
        alert(`IP ${blockIP} has been blocked`);
      }
    } catch (err) {
      console.error('Failed to block IP:', err);
      alert('Failed to block IP');
    } finally {
      setActionLoading(null);
    }
  };

  const handleUnblockIP = async (ip: string) => {
    if (!confirm(`Are you sure you want to unblock ${ip}?`)) return;
    
    setActionLoading(ip);
    try {
      const response = await fetch('/api/security/unblock-ip', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ ip_address: ip })
      });
      
      if (response.ok) {
        setBlockedIPs(prev => prev.filter(item => item.ip !== ip));
        alert(`IP ${ip} has been unblocked`);
      }
    } catch (err) {
      console.error('Failed to unblock IP:', err);
      alert('Failed to unblock IP');
    } finally {
      setActionLoading(null);
    }
  };

  const handleCheckIP = () => {
    if (!checkIP.trim()) return;
    fetchClientScore(checkIP.trim());
    setCheckIP('');
  };

  const getTrustLevelColor = (level: string) => {
    switch (level) {
      case 'trusted': return '#10b981';
      case 'normal': return '#6366f1';
      case 'suspicious': return '#f59e0b';
      case 'blocked': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const getFeatureStatus = (enabled: boolean) => enabled ? '✅ Enabled' : '❌ Disabled';

  const renderOverview = () => (
    <div className="security-overview">
      <div className="overview-header">
        <h3>🛡️ Security Status</h3>
        <span className={`status-badge ${securityStatus?.status === 'operational' ? 'operational' : 'warning'}`}>
          {securityStatus?.status || 'Unknown'}
        </span>
      </div>

      <div className="features-grid">
        <div className="feature-card">
          <span className="feature-icon">⚡</span>
          <div className="feature-info">
            <h4>Rate Limiting</h4>
            <p>{getFeatureStatus(securityStatus?.features?.rate_limiting || false)}</p>
          </div>
        </div>
        <div className="feature-card">
          <span className="feature-icon">🛡️</span>
          <div className="feature-info">
            <h4>SQL Injection Protection</h4>
            <p>{getFeatureStatus(securityStatus?.features?.sql_injection_protection || false)}</p>
          </div>
        </div>
        <div className="feature-card">
          <span className="feature-icon">🚫</span>
          <div className="feature-info">
            <h4>XSS Protection</h4>
            <p>{getFeatureStatus(securityStatus?.features?.xss_protection || false)}</p>
          </div>
        </div>
        <div className="feature-card">
          <span className="feature-icon">🔐</span>
          <div className="feature-info">
            <h4>CSRF Protection</h4>
            <p>{getFeatureStatus(securityStatus?.features?.csrf_protection || false)}</p>
          </div>
        </div>
        <div className="feature-card">
          <span className="feature-icon">🌐</span>
          <div className="feature-info">
            <h4>DDoS Protection</h4>
            <p>{getFeatureStatus(securityStatus?.features?.ddos_protection || false)}</p>
          </div>
        </div>
        <div className="feature-card">
          <span className="feature-icon">📋</span>
          <div className="feature-info">
            <h4>Security Headers</h4>
            <p>{getFeatureStatus(securityStatus?.features?.security_headers || false)}</p>
          </div>
        </div>
      </div>

      <div className="threat-section">
        <h4>🔍 Threat Detection</h4>
        <div className="threat-info">
          <p><strong>Active Monitoring:</strong> {securityStatus?.threat_detection?.active_monitoring ? '✅ Running' : '❌ Stopped'}</p>
          <p><strong>Last Scan:</strong> {securityStatus?.threat_detection?.last_scan || 'Never'}</p>
        </div>
      </div>

      <div className="stats-row">
        <div className="stat-box">
          <span className="stat-number">{blockedIPs.length}</span>
          <span className="stat-label">Blocked IPs</span>
        </div>
        <div className="stat-box">
          <span className="stat-number">{clientScores.length}</span>
          <span className="stat-label">Scanned Clients</span>
        </div>
        <div className="stat-box">
          <span className="stat-number">{Object.keys(headers).length}</span>
          <span className="stat-label">Security Headers</span>
        </div>
      </div>
    </div>
  );

  const renderBlockedIPs = () => (
    <div className="blocked-ips-section">
      <h3>🚫 Blocked IP Addresses</h3>
      
      <div className="block-form">
        <div className="form-row">
          <input
            type="text"
            placeholder="Enter IP address to block (e.g., 192.168.1.1)"
            value={blockIP}
            onChange={(e) => setBlockIP(e.target.value)}
          />
          <select value={blockDuration} onChange={(e) => setBlockDuration(Number(e.target.value))}>
            <option value={60}>5 minutes</option>
            <option value={300}>15 minutes</option>
            <option value={900}>1 hour</option>
            <option value={3600}>6 hours</option>
            <option value={86400}>24 hours</option>
            <option value={-1}>Permanent</option>
          </select>
        </div>
        <div className="form-row">
          <input
            type="text"
            placeholder="Reason (optional)"
            value={blockReason}
            onChange={(e) => setBlockReason(e.target.value)}
          />
          <button 
            className="btn btn-danger"
            onClick={handleBlockIP}
            disabled={!blockIP.trim() || actionLoading === 'blocking'}
          >
            {actionLoading === 'blocking' ? 'Blocking...' : '🚫 Block IP'}
          </button>
        </div>
      </div>

      <div className="blocked-ips-list">
        {blockedIPs.length === 0 ? (
          <div className="empty-state">
            <span className="empty-icon">✅</span>
            <p>No IPs are currently blocked</p>
          </div>
        ) : (
          <table className="ips-table">
            <thead>
              <tr>
                <th>IP Address</th>
                <th>Reason</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {blockedIPs.map((item) => (
                <tr key={item.ip}>
                  <td className="ip-address">{item.ip}</td>
                  <td>{item.reason || 'No reason provided'}</td>
                  <td>
                    <button
                      className="btn btn-success btn-sm"
                      onClick={() => handleUnblockIP(item.ip)}
                      disabled={actionLoading === item.ip}
                    >
                      {actionLoading === item.ip ? 'Unblocking...' : '✅ Unblock'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );

  const renderClientScores = () => (
    <div className="client-scores-section">
      <h3>📊 Client Trust Scores</h3>
      
      <div className="score-check-form">
        <input
          type="text"
          placeholder="Enter IP address to check trust score"
          value={checkIP}
          onChange={(e) => setCheckIP(e.target.value)}
        />
        <button className="btn btn-primary" onClick={handleCheckIP}>
          🔍 Check Score
        </button>
      </div>

      <div className="scores-grid">
        {clientScores.map((score) => (
          <div key={score.ip_address} className="score-card" style={{ borderLeftColor: getTrustLevelColor(score.trust_level) }}>
            <div className="score-header">
              <span className="score-ip">{score.ip_address}</span>
              <span className="score-value" style={{ color: getTrustLevelColor(score.trust_level) }}>
                {score.score}
              </span>
            </div>
            <div className="score-details">
              <span className="trust-level" style={{ color: getTrustLevelColor(score.trust_level) }}>
                {score.trust_level.toUpperCase()}
              </span>
              <span className="recommendation">{score.recommendation}</span>
            </div>
          </div>
        ))}
      </div>

      {clientScores.length === 0 && (
        <div className="empty-state">
          <span className="empty-icon">📊</span>
          <p>Enter an IP address above to check its trust score</p>
        </div>
      )}
    </div>
  );

  const renderSecurityHeaders = () => (
    <div className="headers-section">
      <h3>📋 Security Headers Configuration</h3>
      
      <div className="headers-list">
        {Object.entries(headers).map(([name, info]) => (
          <div key={name} className="header-card">
            <div className="header-info">
              <h4>{name}</h4>
              <p className="header-description">{info.description}</p>
              <p className="header-current">
                <strong>Current:</strong> <code>{info.current}</code>
              </p>
            </div>
            <div className={`header-status ${info.recommended ? 'recommended' : ''}`}>
              {info.recommended ? '✅ Recommended' : '⚠️ Optional'}
            </div>
          </div>
        ))}
      </div>

      <div className="headers-summary">
        <p>
          <strong>{Object.values(headers).filter(h => h.recommended).length}</strong> recommended headers enabled
        </p>
      </div>
    </div>
  );

  if (loading) {
    return <div className="loading">Loading security status...</div>;
  }

  if (error) {
    return (
      <div className="security-error">
        <span className="error-icon">⚠️</span>
        <h3>Access Denied</h3>
        <p>{error}</p>
        <p className="error-hint">This section requires admin privileges.</p>
      </div>
    );
  }

  return (
    <div className="security-management">
      <div className="security-nav">
        <button 
          className={`nav-btn ${activeSection === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveSection('overview')}
        >
          📊 Overview
        </button>
        <button 
          className={`nav-btn ${activeSection === 'blocked' ? 'active' : ''}`}
          onClick={() => setActiveSection('blocked')}
        >
          🚫 Blocked IPs ({blockedIPs.length})
        </button>
        <button 
          className={`nav-btn ${activeSection === 'scores' ? 'active' : ''}`}
          onClick={() => setActiveSection('scores')}
        >
          📊 Trust Scores
        </button>
        <button 
          className={`nav-btn ${activeSection === 'headers' ? 'active' : ''}`}
          onClick={() => setActiveSection('headers')}
        >
          📋 Headers
        </button>
      </div>

      <div className="security-content">
        {activeSection === 'overview' && renderOverview()}
        {activeSection === 'blocked' && renderBlockedIPs()}
        {activeSection === 'scores' && renderClientScores()}
        {activeSection === 'headers' && renderSecurityHeaders()}
      </div>
    </div>
  );
};

export default SecurityManagement;
